// ============================================
// TELA DE BLOQUEIO POR MENSALIDADE EM ATRASO
//
// Último degrau da régua de inadimplência. Reaproveita a linguagem visual da
// autenticação (navy profundo + grid clínico + card em vidro) porque isto é um
// portão de sessão, não uma página de erro: o sistema está íntegro, só o acesso
// está suspenso.
// ============================================

import React, { useState } from 'react';
import { Box, Button, Chip, Divider, Snackbar, Stack, Typography } from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { refreshBillingState, type PlatformBillingState } from '../services/platformBillingStore';
import PlatformActivationPayment, {
  type ActivationState,
} from './platform/PlatformActivationPayment';
import NativeAppBillingNotice from './platform/NativeAppBillingNotice';
import { isKlinviaNativeApp } from '../utils/nativeApp';

const NAVY_DEEP = '#0B1220';
const NAVY_MID = '#1E293B';
const PRIMARY = '#2563EB';
const TEAL = '#2DD4BF';
const SLATE_FAINT = '#94A3B8';

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(ymd: string | null): string {
  if (!ymd) return '—';
  return new Date(`${ymd.slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

const BillingBlockedScreen: React.FC<{ state: PlatformBillingState }> = ({ state }) => {
  const { logout } = useAuth();
  const [checking, setChecking] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);

  const invoice = state.invoice;
  const nativeApp = isKlinviaNativeApp();
  // Contrato obrigatório já assinado: o que falta é o pagamento de ativação, e
  // a tela passa a ser a de pagamento em vez do aviso de inadimplência.
  const activationPending =
    state.blockReason === 'activation_payment' && Boolean(state.activation?.required);

  const copy = (value: string, label: string) => {
    void navigator.clipboard.writeText(value);
    setSnack(`${label} copiado.`);
  };

  const recheck = async () => {
    setChecking(true);
    await refreshBillingState();
    setChecking(false);
    setSnack('Situação reconsultada. Se o pagamento já foi confirmado, o acesso é liberado em instantes.');
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 4 },
        background: `linear-gradient(160deg, ${NAVY_DEEP} 0%, ${NAVY_MID} 100%)`,
        // Grid clínico: papel de prontuário, assinatura visual do produto.
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage:
            'linear-gradient(rgba(148,163,184,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.06) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        },
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 560,
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(59,130,246,0.15)',
          backdropFilter: 'blur(12px)',
          backgroundColor: 'rgba(15,23,42,0.72)',
          boxShadow: '0 24px 64px rgba(2,6,23,0.55)',
          animation: 'billingBlockedIn 0.45s ease-out',
          '@keyframes billingBlockedIn': {
            from: { opacity: 0, transform: 'translateY(8px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        {/* Fio teal: única aposta cromática da tela. */}
        <Box sx={{ height: 2, background: `linear-gradient(90deg, ${TEAL} 0%, ${PRIMARY} 45%, transparent 100%)` }} />

        <Box sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                bgcolor: 'rgba(37,99,235,0.16)',
                color: '#93C5FD',
              }}
            >
              <LockIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                sx={{ color: '#F8FAFC', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}
              >
                {activationPending && !nativeApp ? 'Falta o pagamento do plano' : 'Acesso suspenso'}
              </Typography>
              <Typography variant="body2" sx={{ color: SLATE_FAINT, mt: 0.75 }}>
                {nativeApp
                  ? 'O acesso desta clínica à Klinvia está temporariamente suspenso.'
                  : activationPending
                  ? 'O contrato já está assinado. O sistema é liberado assim que o pagamento da fatura do plano for confirmado.'
                  : `${
                      state.daysOverdue > 0
                        ? `A mensalidade da plataforma está em atraso há ${state.daysOverdue} ${state.daysOverdue === 1 ? 'dia' : 'dias'}.`
                        : 'A mensalidade da plataforma está pendente.'
                    } Regularize o pagamento para liberar o sistema.`}
              </Typography>
            </Box>
          </Stack>

          {nativeApp ? (
            <NativeAppBillingNotice tone="dark" />
          ) : activationPending && (state.canPayActivation || state.canPay) ? (
            <PlatformActivationPayment
              tone="dark"
              initial={(state.activation as ActivationState | null) ?? null}
              onReleased={() => window.location.reload()}
            />
          ) : activationPending ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(148,163,184,0.06)',
                border: '1px solid rgba(148,163,184,0.12)',
              }}
            >
              <Typography variant="body2" sx={{ color: '#E2E8F0' }}>
                Peça ao administrador master da clínica para concluir o pagamento de ativação. Ele
                encontra a cobrança ao entrar no sistema.
              </Typography>
            </Box>
          ) : invoice ? (
            <>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'baseline' }}
                justifyContent="space-between"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(148,163,184,0.06)',
                  border: '1px solid rgba(148,163,184,0.12)',
                }}
              >
                <Box>
                  <Typography variant="overline" sx={{ color: SLATE_FAINT, letterSpacing: '0.08em', fontWeight: 700 }}>
                    Fatura em aberto
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ color: '#F8FAFC', fontWeight: 700, letterSpacing: '-0.02em', mt: 0.25 }}
                  >
                    {formatBRL(invoice.valueCents)}
                  </Typography>
                </Box>
                <Stack spacing={0.5} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                  <Chip
                    size="small"
                    label={`Venceu em ${formatDate(invoice.dueDate)}`}
                    sx={{
                      bgcolor: 'rgba(185,28,28,0.18)',
                      color: '#FCA5A5',
                      fontWeight: 600,
                      border: '1px solid rgba(248,113,113,0.28)',
                    }}
                  />
                </Stack>
              </Stack>

              <Stack spacing={1.25} sx={{ mt: 2.5 }}>
                {invoice.invoiceUrl && (
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<OpenInNewIcon />}
                    href={invoice.invoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      bgcolor: PRIMARY,
                      '&:hover': { bgcolor: '#1D4ED8' },
                      fontWeight: 700,
                      borderRadius: 2,
                      py: 1.25,
                    }}
                  >
                    Abrir fatura e pagar
                  </Button>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  {invoice.pixPayload && (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<CopyIcon />}
                      onClick={() => copy(invoice.pixPayload!, 'Código PIX')}
                      sx={{ color: '#CBD5E1', borderColor: 'rgba(148,163,184,0.3)', borderRadius: 2, fontWeight: 600 }}
                    >
                      Copiar PIX
                    </Button>
                  )}
                  {invoice.identificationField && (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<CopyIcon />}
                      onClick={() => copy(invoice.identificationField!, 'Linha digitável')}
                      sx={{ color: '#CBD5E1', borderColor: 'rgba(148,163,184,0.3)', borderRadius: 2, fontWeight: 600 }}
                    >
                      Linha digitável
                    </Button>
                  )}
                  {invoice.bankSlipUrl && (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      href={invoice.bankSlipUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: '#CBD5E1', borderColor: 'rgba(148,163,184,0.3)', borderRadius: 2, fontWeight: 600 }}
                    >
                      Boleto
                    </Button>
                  )}
                </Stack>
              </Stack>
            </>
          ) : (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(148,163,184,0.06)',
                border: '1px solid rgba(148,163,184,0.12)',
              }}
            >
              <Typography variant="body2" sx={{ color: '#E2E8F0' }}>
                {state.canPay
                  ? 'A fatura não pôde ser carregada agora. Tente reconsultar em instantes ou fale com o suporte da Klinvia.'
                  : 'Peça ao administrador master da clínica para regularizar a mensalidade. Ele encontra a fatura em Assinatura da plataforma.'}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 3, borderColor: 'rgba(148,163,184,0.14)' }} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
            {/* Na ativação o próprio componente de pagamento já traz o "já paguei". */}
            {(nativeApp || !activationPending) && (
              <Button
                startIcon={<RefreshIcon />}
                onClick={recheck}
                disabled={checking}
                sx={{ color: '#93C5FD', fontWeight: 600, justifyContent: 'flex-start' }}
              >
                {checking ? 'Verificando…' : nativeApp ? 'Verificar novamente' : 'Já paguei, verificar novamente'}
              </Button>
            )}
            <Button
              startIcon={<LogoutIcon />}
              onClick={logout}
              sx={{ color: SLATE_FAINT, fontWeight: 600, justifyContent: 'flex-start' }}
            >
              Sair
            </Button>
          </Stack>

          <Typography variant="caption" sx={{ color: 'rgba(148,163,184,0.7)', display: 'block', mt: 2.5 }}>
            {nativeApp
              ? 'Nenhum dado da clínica foi apagado.'
              : activationPending
              ? 'Pagou por outro meio? Fale com a Klinvia: a baixa manual libera o acesso na hora.'
              : 'A emissão e a segunda via de documentos já registrados continuam disponíveis. Nenhum dado da clínica foi apagado.'}
          </Typography>
        </Box>
      </Box>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default BillingBlockedScreen;
